import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, SpinningLoader, Text } from '@frontend/design-system';
import { useInsurancePayerConnectionShallowStore } from '../stores';
import { insuranceTabTrackingIds } from '../tracking-ids';

interface InsuranceCardBannerNotificationProps {
  enhancedVerificationInprogress: boolean;
  isEligibleForWebsiteResp: boolean;
  locationId: string;
  payerID?: string;
}

export const InsuranceCardBannerNotification = ({
  enhancedVerificationInprogress,
  isEligibleForWebsiteResp,
  locationId,
  payerID,
}: InsuranceCardBannerNotificationProps) => {
  const { t } = useTranslation();

  const { setPayerConnectionPayerID } = useInsurancePayerConnectionShallowStore('setPayerConnectionPayerID');

  const { selectedLocationIds } = useAppScopeStore();

  const isSingleLocation = selectedLocationIds.length === 1;

  const { data: payerConnectionDetails, isLoading: isPayerConnectionDetailsLoading } =
    InsuranceDetailQueries.useGetPayerConnectionDetails(locationId, {
      enabled: isEligibleForWebsiteResp && !!payerID && !enhancedVerificationInprogress,
    });

  const navigateToPayerConnectionManagement = useCallback(() => {
    setPayerConnectionPayerID(payerID || '');
    if (isSingleLocation) {
      window.location.hash = 'settings/insurance/payer-connection-management';
    } else {
      window.location.hash = `settings/insurance/payer-connection-management/${locationId}`;
    }
  }, [isSingleLocation, locationId, payerID, setPayerConnectionPayerID]);

  const hasConnectionInitiatedWithPayer = useMemo(() => {
    const payerConnection = payerConnectionDetails?.connections?.find(
      (connection) => connection.payerId === payerID || connection.alternativePayerIds?.includes(payerID || '')
    );
    return !!payerConnection?.id && !!payerConnection?.websiteId;
  }, [payerConnectionDetails?.connections, payerID]);

  const payerNotConnectedMessage = useMemo(
    () => (
      <>
        <Text>
          {t('Payer credentials are missing. Insurance eligibility results are limited until they are connected.')}
        </Text>
        <Button
          css={{ marginTop: theme.spacing(1), padding: 0 }}
          variant='tertiary'
          trackingId={insuranceTabTrackingIds.addPayerConnectionButton}
          onClick={navigateToPayerConnectionManagement}
        >
          {t('Connect Payers')}
        </Button>
      </>
    ),
    [t, navigateToPayerConnectionManagement]
  );

  const payerEligibleForWebsiteResponseMessage = useMemo(
    () => (
      <>
        <Text>
          {t('Currently showing EDI response. Connect your payer credentials to view the full website report.')}
        </Text>
        <Button
          css={{ marginTop: theme.spacing(1), padding: 0 }}
          variant='tertiary'
          trackingId={insuranceTabTrackingIds.addPayerConnectionButton}
          onClick={navigateToPayerConnectionManagement}
          iconName='plus'
        >
          {t('Add Payer Credential')}
        </Button>
      </>
    ),
    [t, navigateToPayerConnectionManagement]
  );

  if (isPayerConnectionDetailsLoading) {
    return <SpinningLoader size='small' />;
  }

  if (enhancedVerificationInprogress) {
    return (
      <BannerNotification
        css={commonBannerStyles}
        status='warn'
        message={t("We're currently retrieving your report. This may take 1-2 minutes.")}
      />
    );
  }

  if (isEligibleForWebsiteResp) {
    return (
      <BannerNotification
        css={commonBannerStyles}
        status={hasConnectionInitiatedWithPayer ? 'warn' : 'error'}
        message={hasConnectionInitiatedWithPayer ? payerEligibleForWebsiteResponseMessage : payerNotConnectedMessage}
      />
    );
  }

  return null;
};

const commonBannerStyles = css({ margin: theme.spacing(2, 0), alignItems: 'flex-start' });
